import { inject, Injectable } from '@angular/core';
import { Address } from '../data/address-config';
import { GachaTeamRegistryABI } from '../generated/abi/gachaTeamRegistry';
import { OnchainStateService } from './onchain-state.service';
import { Wallet } from './wallet';

export type StarterIndex = 0 | 1 | 2;

export const MAX_TEAMS = 16;
// Slot 0 is the player's first team and survives delete operations in the UI
// (chain accepts deleteTeam(0), but the UX guarantees a stable primary).
export const PRIMARY_TEAM_SLOT = 0;

// Mirrors GachaTeamRegistry.sol allocator: lowest unset bit in [0, MAX_TEAMS).
export function nextFreeSlot(liveBitmap: bigint): number {
  const free = ~liveBitmap & ((1n << BigInt(MAX_TEAMS)) - 1n);
  if (free === 0n) throw new Error('TeamCapReached');
  let n = 0;
  let x = free;
  while ((x & 1n) === 0n) { x >>= 1n; n++; }
  return n;
}

@Injectable({
  providedIn: 'root',
})
export class TeamService {
  private wallet = inject(Wallet);
  private onchainState = inject(OnchainStateService);

  readonly ownedMonIndices = this.onchainState.ownedMonIndices;
  readonly monIndicesForTeam = this.onchainState.monIndicesForTeam;
  readonly matchmakerStatus = this.onchainState.matchmakerStatus;
  readonly hasTeam = this.onchainState.hasTeam;

  // On the create path, `slot` is ignored — the chain picks lowest-unset-bit
  // and we predict the same slot client-side to seed local state.
  async setTeam(slot: bigint, monIndices: bigint[]): Promise<void> {
    const live = this.onchainState.liveBitmap();
    const isLive = (live & (1n << slot)) !== 0n;

    if (!isLive) {
      const predicted = nextFreeSlot(live);
      await this.wallet.sendRealtimeRawTransaction({
        address: Address.GACHA_TEAM_REGISTRY,
        abi: GachaTeamRegistryABI,
        functionName: 'createTeam',
        args: [monIndices],
        ...this.wallet.burnerTx,
      });
      this.onchainState.appendCreatedTeam(predicted, monIndices);
    } else {
      const positions = monIndices.map((_, i) => BigInt(i));
      await this.wallet.sendRealtimeRawTransaction({
        address: Address.GACHA_TEAM_REGISTRY,
        abi: GachaTeamRegistryABI,
        functionName: 'updateTeam',
        args: [slot, positions, monIndices],
        ...this.wallet.burnerTx,
      });
      this.onchainState.setMonIndicesForTeam(Number(slot), monIndices);
    }
  }

  async deleteTeam(slot: bigint): Promise<void> {
    await this.wallet.sendRealtimeRawTransaction({
      address: Address.GACHA_TEAM_REGISTRY,
      abi: GachaTeamRegistryABI,
      functionName: 'deleteTeam',
      args: [slot],
      ...this.wallet.burnerTx,
      gas: 80000n,
    });
    this.onchainState.removeTeam(Number(slot));
  }

  // Pass facetId=0 to unequip.
  async assignFacet(monId: bigint, facetId: number): Promise<void> {
    await this.wallet.sendRealtimeRawTransaction({
      address: Address.GACHA_TEAM_REGISTRY,
      abi: GachaTeamRegistryABI,
      functionName: 'assignFacets',
      args: [[monId], [facetId]],
      ...this.wallet.burnerTx,
      gas: 150000n,
    });
  }

  // `bitmap` is an 8-bit lane set (1..4 bits).
  async assignMoves(monId: bigint, bitmap: number): Promise<void> {
    await this.wallet.sendRealtimeRawTransaction({
      address: Address.GACHA_TEAM_REGISTRY,
      abi: GachaTeamRegistryABI,
      functionName: 'assignMoves',
      args: [[monId], [bitmap]],
      ...this.wallet.burnerTx,
      gas: 150000n,
    });
  }

}
